import {FormGroup, Validators} from '@angular/forms';
import {Component, Input, Output, EventEmitter} from '@angular/core';
import {ErrorMessage} from '../../models/error-message';
import {CepService} from '../../services/cep/cep.service';
import {ICepResponse} from '../../interfaces/ICepResponse';
import {OnInit} from '@angular/core';
import PatternsUtils from '../../../utils/patterns.utils';
import {states} from '../../../utils/constants.utils';
import {catchError, concatMap, from, map, Observable, throwError} from 'rxjs';
import {StatusService} from '../../services/authentication/status.service';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
  selector: 'app-cep-search',
  templateUrl: './cep-search.component.html',
  styleUrls: ['./cep-search.component.scss'],
})
export class CepSearchComponent implements OnInit {
  @Input() title: string;

  @Input() formParent: FormGroup;

  @Input() formControlNames: string[];

  @Input() enableFieldsAfterDirty?: boolean = false;

  @Output() formParentChange = new EventEmitter<FormGroup>();

  @Output() isLoading = false;

  cepInformations: ICepResponse = {
    zipCode: '',
    uf: '',
    city: '',
    street: '',
    district: '',
    state: ''
  };

  infoCep = 'caso o logradouro não possua CEP, utilize do município, distrito ou povoado';

  statesOptions = [...states];

  citiesOptions: string[] = [];

  citiesIsLoading = false;

  constructor(private cepService: CepService, private statusService: StatusService) {
  }

  ngOnInit(): void {
    this.disableAddressEdit();

    this.formParent
      .get(this.formControlNames[3])
      ?.setValidators([
        Validators.pattern(PatternsUtils.ONLY_NUMBER_AND_LETTERS),
        Validators.required,
      ]);

    this.formParent
      .get(this.formControlNames[4])
      ?.setValidators([
        Validators.pattern(PatternsUtils.ONLY_NUMBER_AND_LETTERS),
        Validators.required,
      ]);

    this.formParent
      .get(this.formControlNames[5])
      ?.setValidators([Validators.pattern(PatternsUtils.ONLY_NUMBERS), Validators.required]);

    this.formParent
      .get(this.formControlNames[6])
      ?.setValidators([Validators.pattern(PatternsUtils.ONLY_NUMBER_AND_LETTERS)]);

    this.formParent.statusChanges.subscribe(() => {
      this.formParentChange.emit(this.formParent);
    });

    this.formParent.get(this.formControlNames[0])?.statusChanges.subscribe(() => {
      if (this.formParent.get(this.formControlNames[0])?.invalid) {
        this.clearFields();
        this.disableAddressEdit();
      }
    });

    this.checkAddress({clearFieldsOnError: false});
  }

  findOption(value: string) {
    return this.statesOptions.find(({state, uf}) => value === state || value === uf);
  }

  checkAddress(
    {clearFieldsOnError}: { clearFieldsOnError?: boolean } = {clearFieldsOnError: false},
  ) {
    if (
      this.formParent.get(this.formControlNames[0])?.valid &&
      this.formParent.get(this.formControlNames[0])?.value
    ) {
      this.isLoading = true;
      this.changeAddress(this.formParent.get(this.formControlNames[0])?.value).subscribe({
        next: ({address, cities}) => {
          this.isLoading = false;
          this.cepInformations = address.data;
          this.citiesOptions = cities;
        },
        complete: () => {
          this.isLoading = false;
          this.fillAddressFields();
        },
        error: err => {
          this.isLoading = false;
          if (clearFieldsOnError) {
            this.clearFields();
          }
          this.disableAddressEdit();
          this.showError(err);
        },
      });
    } else {
      this.isLoading = false;
      this.clearFields();
      this.disableAddressEdit();
    }
  }

  changeAddress(cep: string) {

    return this.cepService.getCepInformation(cep).pipe(

      concatMap((address) =>

        this.getCities(address.data.uf.trim()).pipe(
          map(cities => ({
            address,
            cities
          })),
          catchError(err =>
            throwError(() => {
              if (err instanceof Error) {
                return new Error(err.message);
              }
              return new Error('Erro ao buscar cidades.');
            })
          )
        )
      ),
      catchError(err => {
        this.formParent.get(this.formControlNames[1])?.setValue('');
        this.formParent.get(this.formControlNames[1])?.enable();
        this.formParent.get(this.formControlNames[2])?.setValue('');
        this.formParent.get(this.formControlNames[2])?.enable();

         const cepControl = this.formParent.get(this.formControlNames[0]);
      if (cepControl) {
        const currentErrors = cepControl.errors || {};
        cepControl.setErrors({
          ...currentErrors,
          invalidCep: true,
        });
        cepControl.markAsTouched();

      }

        return throwError(() => new Error(err?.message || err || 'Error on change address'));
      })
    );
  }


  getCities(uf: string): Observable<string[]> {
    return from(this.cepService.getCities(uf)).pipe(
      map(response => response.data?.map((city: { name: string }) => city.name) || [])
    );
  }

  handleChangeState(event: { value: string }) {
    const option = this.findOption(event.value);
    if (option) {
      this.isLoading = true;
      this.getCities(option.uf).subscribe({
        next: cities => {
          this.citiesOptions = [...cities];
        },
        complete: () => {
          this.isLoading = false;
          this.formParent.get(this.formControlNames[2])?.setValue(option?.state);
          this.formParent.get(this.formControlNames[2])?.enable();
        },
        error: err => {
          this.isLoading = false;
          this.citiesOptions = [];
          this.showError(err);
        },
      });
    }
  }

  showError(err: unknown) {
    let errorMessage = 'Error ao buscar cidades';
    if (err instanceof HttpErrorResponse) {
      errorMessage = err.error?.message || err.error?.errors[0]?.message || errorMessage;
    }
    this.statusService.showToast('error', errorMessage);
  }

  fillAddressFields() {
    if (this.cepInformations.state?.trim()) {
      this.formParent.get(this.formControlNames[1])?.setValue(this.cepInformations.state.trim());
      this.formParent.get(this.formControlNames[1])?.disable();
    } else {
      this.formParent.get(this.formControlNames[1])?.enable();
    }

    if (
      this.cepInformations.city?.trim() &&
      this.citiesOptions.some(city => city === this.cepInformations.city?.trim())
    ) {
      this.formParent.get(this.formControlNames[2])?.setValue(this.cepInformations.city.trim());
      this.formParent.get(this.formControlNames[2])?.disable();
    } else {
      this.formParent.get(this.formControlNames[2])?.enable();
    }

    if (this.cepInformations.district?.trim()) {
      this.formParent.get(this.formControlNames[3])?.setValue(this.cepInformations.district.trim());
      this.formParent.get(this.formControlNames[3])?.disable();
    } else {
      this.formParent.get(this.formControlNames[3])?.enable();
    }

    if (this.cepInformations.street?.trim()) {
      this.formParent
        .get(this.formControlNames[4])
        ?.setValue(this.cepInformations.street.trim());
      this.formParent.get(this.formControlNames[4])?.disable();
    } else {
      this.formParent.get(this.formControlNames[4])?.enable();
    }

    this.formParent.get(this.formControlNames[5])?.enable();
    this.formParent.get(this.formControlNames[6])?.enable();
  }

  disableAddressEdit() {
    this.formParent.get(this.formControlNames[1])?.disable(); //estado
    this.formParent.get(this.formControlNames[2])?.disable(); //cidade
    this.formParent.get(this.formControlNames[3])?.disable(); //bairro
    this.formParent.get(this.formControlNames[4])?.disable(); //endereco
    this.formParent.get(this.formControlNames[5])?.disable(); //número
    this.formParent.get(this.formControlNames[6])?.disable(); //complemento
  }

  enableAddresEdit() {
    this.formParent.get(this.formControlNames[1])?.enable(); //estado
    this.formParent.get(this.formControlNames[2])?.enable(); //cidade
    this.formParent.get(this.formControlNames[3])?.enable(); //bairro
    this.formParent.get(this.formControlNames[4])?.enable(); //endereco
    this.formParent.get(this.formControlNames[5])?.enable(); //número
    this.formParent.get(this.formControlNames[6])?.enable(); //complemento
  }

  clearFields() {
    this.formParent.get(this.formControlNames[1])?.setValue(''); //estado
    this.formParent.get(this.formControlNames[2])?.setValue(''); //cidade
    this.formParent.get(this.formControlNames[3])?.setValue(''); //bairro
    this.formParent.get(this.formControlNames[4])?.setValue(''); //endereco
    this.formParent.get(this.formControlNames[5])?.setValue(''); //número
    this.formParent.get(this.formControlNames[6])?.setValue(''); //complemento
  }

  getErrorInvalid(label: string): string {
    return ErrorMessage.getTextInvalid(label);
  }


  getCepFieldError(controlName: string, label: string): string {
  const control = this.formParent.get(controlName);
  return ErrorMessage.getCepFieldError(control, label);
}


  getErrorMandatory(label: string): string {
    return ErrorMessage.getTextMandatory(label);
  }
}
